import ApiController from './ApiController';

/**
 * Usuário vinculado à empresa
 * @typedef Usuario
 * @property {String} nome Nome do usuário
 * @property {String} email Email do usuário
 * @property {String} telefone Número de telefone do usuário
 */

/**
 * Dados da empresa cadastrada
 * @typedef Empresa
 * @property {Number} id Identificador único da empresa
 * @property {String} nome Nome da empresa
 * @property {String} cpfCnpj CPF ou CNPJ da empresa
 * @property {Number} tipoEmpresa Identificação do tipo da empresa
 * @property {Date} dataLimite Data limite da empresa para utilização
 * @property {Usuario} usuario Usuário associado à empresa
 * @property {Date} dataCadastro Data em que foi inserida
 * @property {Date} dataAlteracao Data em que foi feita a última alteração
 */

/**
 * Mensagem de erro retornada pela API ou da validação local
 * @typedef Erro
 * @property {String} codigo Tipo do erro
 * @property {String} campo Campo em que o erro ocorreu
 * @property {String} mensagem Mensagem de erro em português
 */

/**
 * Lista de empresas retornada pela API com informações de paginação
 * @typedef Listagem
 * @property {Boolean} sucesso Indica se a requisição foi executada
 * @property {Array<Erro>} erros Listagem dos erros retornados
 * @property {Array<Empresa>} items Em caso de sucesso contém as empresas retornadas
 * @property {Number} page Número da página
 * @property {Number} count Quantidade total de registros
 * @property {Number} pageCount Quantidade de páginas disponíveis
 * @property {Number} pageSize Quantidade de registros por página
 */

/**
 * Objeto de retorno dos métodos que manipulam apenas uma empresa
 * @typedef Retorno
 * @property {Boolean} sucesso Indica se a requisição foi executada
 * @property {Array<Erro>} erros Listagem dos erros retornados
 * @property {Number} id Identificador único da empresa
 * @property {String} nome Nome da empresa
 * @property {String} cpfCnpj CPF ou CNPJ da empresa
 * @property {Number} tipoEmpresa Identificação do tipo da empresa
 * @property {Date} dataLimite Data limite da empresa para utilização
 * @property {Usuario} usuario Usuário associado à empresa
 * @property {Date} dataCadastro Data em que foi inserida
 * @property {Date} dataAlteracao Data em que foi feita a última alteração
 */

/**
 * Objeto de retorno da lista sem paginação
 * @typedef RetornoSelecao
 * @property {Boolean} sucesso Indica se a requisição foi executada
 * @property {Array<Empresa>} items Lista de empresas retornadas
 */

/**
 * Controller para incluir, atualizar e listar empresas consumindo a API
 */
class EmpresaController {

    constructor() {
        this.apiController = new ApiController();
    }

    /**
     * Lista as empresas cadastradas, paginando e filtrando de acordo com os parâmetros
     * @param {Number} page Número da página da listagem
     * @param {Number} pageSize Quantidade de registros que devem ser retornados
     * @param {Object} params Filtros adicionais
     * @param {String} params.nome Filtro de nome da empresa
     * @param {String} params.cpfCnpj Filtro do cpf da empresa
     * @returns {Promise<Listagem>} Retorna a listagem das empresas encontradas
     */
    async listar(page, pageSize, { nome, cpfCnpj }) {
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.get('/empresa', { page, pageSize, nome, cpfCnpj });
            //Marca que houve retorno com sucesso
            result.sucesso = true;
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Consulta os dados de uma única empresa na API de acordo com o id informado
     * @param {Number} id Id da empresa a ser exibida
     * @returns {Promise<Retorno>} Retorna os dados da empresa
     */
    async obter(id) {
        //Verifica se o ID está preenchido
        if (!id)
            return { sucesso: false, erros: [ { campo: 'id', mensagem: 'É necessário' } ] };
        
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.get('/empresa/' + id.toString());
            //Marca que houve retorno com sucesso
            result.sucesso = true;
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Adiciona uma nova empresa na API
     * @param {Empresa} empresa Dados da empresa a ser incluída
     * @returns {Promise<Retorno>} Retorna um objeto com a propriedade sucesso
     */
    async salvar(empresa) {
        const erros = this._validarCampos(empresa, false);
            
        //Retorna os erros para a aplicação
        if (erros.length > 0)
            return { sucesso: false, erros };

        try {
            //Monta o corpo da requisição
            const body = this._extrairCampos(empresa);
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.post('/empresa', body);
            //Marca que houve retorno com sucesso
            result.sucesso = true;
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Atualiza os dados de uma empresa na API
     * @param {Empresa} empresa Dados da empresa a ser atualizada
     * @returns {Promise<Retorno>} Retorna um objeto com a propriedade sucesso
     */
    async atualizar(empresa) {
        const erros = this._validarCampos(empresa, true);
            
        if (erros.length > 0)
            return { sucesso: false, erros };

        try {
            //Monta o corpo da requisição
            const body = this._extrairCampos(empresa);
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.put('/empresa/' + empresa.id.toString(), body);
            //Marca que houve retorno com sucesso
            result.sucesso = true;
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Lista as empresas cadastradas, sem paginação, para campos de seleção
     * @returns {Promise<RetornoSelecao>} Retorna a listagem das empresas encontradas
     */
    async listarSelecao() {
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: items } = await this.apiController.get('/empresa', { pagination: false });
            //Marca que houve retorno com sucesso
            const result = {
                sucesso: true,
                items
            };
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Valida os campos da empresa e do usuário associado
     * @param {Empresa} empresa Objeto a ser validado
     * @param {Boolean} atualizacao Indica se o registro está sendo incluído ou atualizado
     * @returns {Array<Erro>} Retorna a lista com os erros encontrados na requisição
     */
    _validarCampos(empresa, atualizacao) {
        var erros = [];

        //Verifica se a empresa está sendo preenchida
        if (!empresa) {
            erros.push({ campo: '', mensagem: 'Empresa é necessária' });
        } else {
            //Verifica se o id está preenchido
            if (atualizacao && !empresa.id) 
                erros.push({ campo: 'id', mensagem: 'É necessário' });
            //Verifica se o nome está preenchido
            if (!empresa.nome)
                erros.push({ campo: 'nome', mensagem: 'É necessário' });
            //Verifica se o CPF/CNPJ está preenchido
            if (!empresa.cpfCnpj)
                erros.push({ campo: 'cpfCnpj', mensagem: 'É necessário' });
            //Verifica se o tipo da empresa está preenchido
            if (!empresa.tipoEmpresa)
                erros.push({ campo: 'tipoEmpresa', mensagem: 'É necessário' });
            //Verifica se a data limite está preenchida
            if (!empresa.dataLimite)
                erros.push({ campo: 'dataLimite', mensagem: 'É necessário' });
            //Verifica se o usuário está preenchido
            if (!empresa.usuario) {
                erros.push({ campo: 'usuario', mensagem: 'É necessário' });
            } else {
                //Verifica se o nome do usuário está preenchido
                if (!empresa.usuario.nome)
                    erros.push({ campo: 'usuario.nome', mensagem: 'É necessário' });
                //Verifica se o email ou telefone do usuário estão preenchidos
                if (!empresa.usuario.email && !empresa.usuario.telefone) {
                    erros.push({ campo: 'usuario.email', mensagem: 'É necessário' });
                    erros.push({ campo: 'usuario.telefone', mensagem: 'É necessário' });
                }
                //Verifica se o id está preenchido
                if (!atualizacao && !empresa.usuario.senha) 
                    erros.push({ campo: 'usuario.senha', mensagem: 'É necessário' });
            }
        }


        return erros;
    }

    /**
     * Extrai apenas os dados que serão enviados na requisição
     * @param {Empresa} empresa Objeto original contendo os dados
     * @returns {Empresa} Objeto apenas com os campos da requisição
     */
    _extrairCampos(empresa) {
        const result = {
            nome: empresa.nome,
            cpfCnpj: empresa.cpfCnpj,
            tipoEmpresa: empresa.tipoEmpresa,
            dataLimite: empresa.dataLimite,
            usuario: {
                nome: empresa.usuario.nome
            }
        };

        if (empresa.logo)
            result.logo = empresa.logo;
        if (empresa.usuario.senha)
            result.usuario.senha = empresa.usuario.senha;
        if (empresa.usuario.email)
            result.usuario.email = empresa.usuario.email;
        if (empresa.usuario.telefone)
            result.usuario.telefone = empresa.usuario.telefone;

        return result;
    }

}

export default EmpresaController;